import {useEffect} from 'react';

import {
    useNavigate,
    useParams
} from 'react-router-dom';

import {Stack} from '@mantine/core';

import {RecordForm} from '../forms/RecordForm';

import {
    FormPageLayout,
    PlatformEditPageGrid,
    PlatformLoader,
} from '@flexinet/ui-components';
import {Profile} from '../profile/Profile';
import {PipelineStagesPanel} from '../profile/PipelineStagesPanel';
import {DeploymentAccountsPanel} from '../profile/DeploymentAccountsPanel';

import {EditPageNavigation} from '../navigation/EditPageNavigation';
import {usePipeline} from '../hooks/usePipeline.ts';
import {usePipelinesSelection} from '../hooks/usePipelinesSelection.ts';
import {useEditPageTools} from '../hooks/useEditPageTools';
import {StackStatusPanel} from '../components/StackStatusPanel';
import {useVerifiedPermissions} from '../../../../hooks/useVerifiedPermissions';
import {rbacActions} from '../../../../config/rbac/actions.ts';
import {useOnUpdatePipelineSubscription} from '../hooks/useOnUpdatePipelineSubscription';


export const EditPage = () => {

    const {id: pipelineId} = useParams();

    if (typeof pipelineId === 'undefined') {
        throw new Error('pipelineId is required parameter');
    }

    const navigate = useNavigate();
    const onCancel = () => {
        navigate(`/pipelines/${pipelineId}`);
    }

    const {
        record: pipeline,
        updateRecord,
    } = usePipeline(pipelineId);

    const {handleClearSelection} = usePipelinesSelection();

    const {
        mode,
        tools,
    } = useEditPageTools(pipeline);

    const {allowedActions} = useVerifiedPermissions();
    const canViewStackStatus = allowedActions.includes(rbacActions.GetStackStatus);

    useOnUpdatePipelineSubscription();


    useEffect(() => {
        handleClearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only effect: deliberately runs once, adding deps would re-fire it
    }, []);

    if (!pipeline) {
        return <PlatformLoader message='Loading pipeline...Please wait'/>;
    }

    return (
        <FormPageLayout
            title={pipeline.name ? pipeline.name : pipeline.id}
            pageNavigation={<EditPageNavigation component={pipeline}/>}
            tools={tools}
        >


            {/* View mode: the record summary goes left, where the grid can
                collapse it out of the way, and the operational panels go right
                where the reading actually happens. Collapsing is what the
                hand-rolled Grid could not offer, and the state persists per
                operator. */}
            {mode === 'view' ?

                <PlatformEditPageGrid
                    leftContent={<Profile record={pipeline}/>}
                    rightContent={
                        <Stack gap='xl'>
                            {canViewStackStatus && pipeline.stackName && (
                                <StackStatusPanel stackName={pipeline.stackName}/>
                            )}
                            <PipelineStagesPanel record={pipeline}/>
                            <DeploymentAccountsPanel record={pipeline}/>
                        </Stack>
                    }
                />

                :
                <RecordForm
                    record={pipeline}
                    handleSubmit={updateRecord}
                    handleCancel={onCancel}
                />}


        </FormPageLayout>
    );
};
